import type { InitialCalendarPlacement } from "../calendarPlacement";
import type { AccountPostInfoDefaults } from "./postInfoDefaults";
import type {
  PublicationModel,
  SocialProviderType,
} from "../../../types/models";
import { SLOT_PRIORITY } from "../../../utils/publicationDistribution";

export type {
  PublicationChangeSet,
  PublicationFlowMode,
  PublicationOperationCandidate,
  PublicationUpdateCandidate,
  VideoPublicationState,
} from "../types";

export type { PublicationModel };

export type AccountCadence = {
  maxPostsPerDay: number;
  allowedISODays: number[];
  preferredTimeSlotId?: string;
};

export const DEFAULT_ACCOUNT_CADENCE: AccountCadence = {
  maxPostsPerDay: 1,
  allowedISODays: [1, 2, 3, 4, 5, 6, 7],
};

export function getAccountCadence(
  accountCadences: Map<string, AccountCadence>,
  accountId: string,
): AccountCadence {
  return accountCadences.get(accountId) ?? { ...DEFAULT_ACCOUNT_CADENCE };
}

export type PublicationClassification = { anchored: boolean };

// Session-level preferred posting hours. The stored array is a pool: its
// order (and DEFAULT_PREFERRED_HOURS' slot-priority order — Evening,
// Afternoon, Midday, Morning, Late Night) only decides which hours ENTER
// the active window as cadence rises. Consumption is chronological: the
// window is sorted, and an account posting k times a day uses its earliest
// k hours.
export const DEFAULT_PREFERRED_HOURS = SLOT_PRIORITY.map(
  (slot) => slot.preferredHour,
);

export function getPreferredHoursForCadence(
  preferredHours: number[],
  maxPostsPerDay: number,
): number[] {
  // A pool may hold an hour twice (older saves): once is enough.
  const padded = [...new Set(preferredHours)];
  for (const fallback of DEFAULT_PREFERRED_HOURS) {
    if (padded.length >= maxPostsPerDay) break;
    if (!padded.includes(fallback)) padded.push(fallback);
  }
  return padded.slice(0, Math.max(1, maxPostsPerDay)).sort((a, b) => a - b);
}

// The session's daily posting times: the stored pool sized to the highest
// cadence among selected accounts, chronologically sorted. Both the header
// pickers and the engine must consume THIS list — deriving per-account
// hours from the raw pool instead would let a 1/day account land on a
// different hour than the earliest one the header shows.
export function getSessionPreferredHours(session: {
  config: { preferredHours: number[] };
  accountCadences: Map<string, AccountCadence>;
  local: { selectedAccountIds: string[] };
}): number[] {
  const maxPostsPerDay = Math.max(
    1,
    ...session.local.selectedAccountIds.map(
      (id) => getAccountCadence(session.accountCadences, id).maxPostsPerDay,
    ),
  );
  return getPreferredHoursForCadence(
    session.config.preferredHours,
    maxPostsPerDay,
  );
}

export type VideoClassification = {
  anchored: boolean;
  hasPublishedPub: boolean;
  hasScheduledPub: boolean;
  hasEditablePub: boolean;
  hasOnlyPublishedPubs: boolean;
  isAccountComplete: boolean;
};

export type SchedulingConfig = { startFrom: Date; preferredHours: number[] };

/** Absent provider means inherit the session pool. */
export type ProviderAccountOverride =
  | { kind: "pin"; accountId: string }
  | { kind: "exclude" };
export type VideoAccountOverride = Record<string, ProviderAccountOverride>;

export type BaselineState = {
  publications: PublicationModel[];
  orderedVideoIds: string[];
  selectedAccountIds: string[];
  videoAccountOverrides: Record<string, VideoAccountOverride>;
  perVideoState: Record<string, { localPublications: PublicationModel[] }>;
};

export type LocalSessionState = {
  orderedVideoIds: string[];
  selectedAccountIds: string[];
  videoAccountOverrides: Record<string, VideoAccountOverride>;
  perVideoState: Record<string, { localPublications: PublicationModel[] }>;
};

export type VideoCaptions = Record<string, string | null>;

/** What the user came to do. `mode` is the engine's concern (edit mode
 *  carries persisted anchors, and context rows force it); `entry` is the
 *  copy's concern (the shell title). They diverge when a "Schedule posts"
 *  session rides folder context rows along: mode edit, entry schedule. */
export type SchedulingEntry = "schedule" | "edit";

export type SchedulingSession = {
  mode: "create" | "edit";
  entry: SchedulingEntry;
  config: SchedulingConfig;
  entryConfig: SchedulingConfig;
  accountCadences: Map<string, AccountCadence>;
  entryAccountCadences: Map<string, AccountCadence>;
  baseline: BaselineState;
  entryState: LocalSessionState;
  local: LocalSessionState;
  existingPublicationIds: Set<string>;
  crossProjectOccupied: Map<string, Set<number>>;
  videoCaptions: Record<string, VideoCaptions>;
  accountProviders: Record<string, string>;
  /** Payload fields new drafts on an account start from (Instagram's
   *  Share to feed follows the account's latest choice). */
  accountPostInfoDefaults?: AccountPostInfoDefaults;
  customDatetimeKeys: Set<string>;
  entryCustomDatetimeKeys: Set<string>;
  /** Navigation metadata only; context and entry share coverage rules. */
  contextVideoIds: Set<string>;
  preStartFromSnapshot?: LocalSessionState;
};

export type DirtyState = {
  isDirty: boolean;
  dirtyVideoIds: Set<string>;
  /** Server-relative work, including drafts generated on open. */
  serverChangeSet?: {
    createdCount: number;
    updatedCount: number;
    removedCount: number;
    totalCount: number;
  };
  changeSet: {
    createdCount: number;
    updatedCount: number;
    removedCount: number;
    /** The subset of `removedCount` that deletes a persisted post — the
     *  removals that actually reach the server (§12). */
    persistedRemovedCount: number;
    totalCount: number;
  };
  changeSummary: string | null;
};

export type CTAState = {
  /** Always rendered (§12: the commit corner is never empty); only
   *  `primaryEnabled` moves. With nothing to submit the label is the
   *  entry's own verb, disabled. */
  primaryLabel: string;
  /** What the primary act IS, for hosts that re-word the label: the form
   *  forces an edit entry's corner to the bare "Save" — except when a
   *  removals-only delta's "Remove N posts" is the act, which the confirm
   *  dialog then repeats instead of "Save changes?". */
  primaryIntent: "schedule" | "save" | "remove";
  primaryEnabled: boolean;
  /** Not rendered since undo/redo replaced the reset arrow (§12); kept
   *  until the diff collapse retires them with the counted dirty diff. */
  showReset: boolean;
  resetDisabled: boolean;
  showChangeSummary: boolean;
  /** The entry's word (§2). Not rendered since the scope chip took the
   *  title slot (§12: the shorts picker on the edit face, the project chip
   *  on the read face); kept as the entry's one derived name — the
   *  Schedule confirm and tests still read it. */
  shellTitle: "Schedule posts" | "Edit posts";
};

export type QueueTileState = {
  /** The row's one state word (§13.2): its next upcoming slot, live
   *  history, a failed attempt with nothing schedulable beside it, or a
   *  draft. */
  displayStatus: "scheduled" | "published" | "draft" | "failed";
  isDraggable: boolean;
  /** The row's date: a scheduled row's next upcoming slot (the earliest
   *  future account-level post), a failed row's latest attempt. */
  scheduledDate: Date | null;
  /** Posts the publisher gave up on (isFailedPub), whatever the row's
   *  word: a "failed" row is made of them, and a row that still has a
   *  plan says "N failed" beside its title (selectQueueRowMeta). */
  failedCount: number;
};

export type SchedulingAction =
  | { type: "APPLY_COVERAGE_PROPOSAL"; base: SchedulingSession; proposal: SchedulingSession }
  | {
      type: "INIT_CREATE";
      initialPlacement?: InitialCalendarPlacement;
      videoIds: string[];
      accountIds: string[];
      config: SchedulingConfig;
      perVideoState: Record<string, { localPublications: PublicationModel[] }>;
      accountCadences: Map<string, AccountCadence>;
      crossProjectOccupied: Map<string, Set<number>>;
      videoCaptions: Record<string, VideoCaptions>;
      accountProviders: Record<string, string>;
      accountPostInfoDefaults?: AccountPostInfoDefaults;
      /** Absent → derived from persisted owners. `{}` keeps tests that
       *  pass an empty map from growing pins. */
      videoAccountOverrides?: Record<string, VideoAccountOverride>;
    }
  | {
      type: "INIT_EDIT";
      initialPlacement?: InitialCalendarPlacement;
      videoIds: string[];
      accountIds: string[];
      publications: PublicationModel[];
      config: SchedulingConfig;
      perVideoState: Record<string, { localPublications: PublicationModel[] }>;
      videoAccountOverrides?: Record<string, VideoAccountOverride>;
      /** Entry videos that already had committed posts when the dialog
       *  opened. The session is an edit entry only when EVERY entry video
       *  (outside `contextVideoIds`) is listed here AND INIT generated no
       *  draft; a selection mixing committed and new shorts, or a
       *  context-only edit mode, is a schedule entry. A committed entry
       *  short opens without drafts (§6), so an edit URL over a saved plan
       *  is an edit entry. Ids outside the session are ignored. */
      initialEditVideoIds: string[];
      accountCadences: Map<string, AccountCadence>;
      crossProjectOccupied: Map<string, Set<number>>;
      videoCaptions: Record<string, VideoCaptions>;
      accountProviders: Record<string, string>;
      accountPostInfoDefaults?: AccountPostInfoDefaults;
      contextVideoIds?: string[];
    }
  | {
      type: "ADD_GLOBAL_ACCOUNT";
      accountId: string;
      provider: SocialProviderType;
      ownerId: string;
    }
  | { type: "REMOVE_GLOBAL_ACCOUNT"; accountId: string }
  | {
      type: "ADD_VIDEO_ACCOUNT";
      videoId: string;
      accountId: string;
      provider: SocialProviderType;
      ownerId: string;
    }
  | { type: "REMOVE_VIDEO_ACCOUNT"; videoId: string; accountId: string }
  | { type: "REMOVE_PUBLICATION"; videoId: string; publicationId: string }
  | { type: "RESTORE_VIDEO_PROVIDER"; videoId: string; provider: string }
  | {
      type: "REASSIGN_VIDEO_ACCOUNT";
      videoId: string;
      fromAccountId: string;
      toAccountId: string;
      provider: SocialProviderType;
      ownerId: string;
    }
  /** The three settings commands take `replanSaved` (§11): true un-anchors
   *  the saved posts they would move (computeReplan) — each re-lays from
   *  its own account's earliest saved date, or from the picked date for a
   *  start-from change; absent, saved posts keep their times and only
   *  drafts re-slot. */
  | { type: "CHANGE_START_FROM"; date: Date; replanSaved?: boolean }
  | { type: "CHANGE_PREFERRED_HOURS"; hours: number[]; replanSaved?: boolean }
  | {
      type: "CHANGE_ACCOUNT_CADENCE";
      accountId: string;
      cadence: AccountCadence;
      replanSaved?: boolean;
    }
  | {
      type: "CHANGE_PUBLICATION_SCHEDULE_DATE";
      videoId: string;
      accountId: string;
      publicationId?: string;
      date: Date;
    }
  | {
      type: "CLEAR_PUBLICATION_SCHEDULE_DATE";
      videoId: string;
      accountId: string;
      publicationId?: string;
    }
  | {
      type: "REORDER_QUEUE";
      orderedVideoIds: string[];
      /** The row the user dragged (§13.6): the rows whose rank changed
       *  re-lay list-insert style, saved posts included
       *  (`computeReorderReplan`). Absent = a plain reorder, saved posts
       *  anchored. */
      movedVideoId?: string;
    }
  | {
      type: "ADD_VIDEO";
      videoId: string;
      perVideoState: { localPublications: PublicationModel[] };
      videoCaptions?: VideoCaptions;
    }
  | { type: "REMOVE_VIDEO"; videoId: string }
  | {
      type: "UPDATE_PUBLICATION_PAYLOAD";
      videoId: string;
      accountId: string;
      publicationId?: string;
      postInfo: unknown;
    }
  | { type: "RESET_ALL" }
  | { type: "RESET_VIDEO"; videoId: string }
  | { type: "RESET_VIDEO_ACCOUNTS"; videoId: string }
  | { type: "RESET_VIDEO_SCHEDULE"; videoId: string }
  | { type: "REVERT_START_FROM" };
